/**
 * Lazy facade over notes-fragment-serializer.js.
 *
 * The real serializer builds a headless TipTap editor, dragging the whole
 * ProseMirror stack into any bundle that imports it statically — including
 * yjs-doc.js on the boot path. This facade keeps the boot bundle clean:
 * callers keep their synchronous call signatures, and the actual module is
 * dynamic-imported before it can be needed.
 *
 * The load guarantee: CRDTBridge.init() awaits ensureNotesSerializer() for
 * docs whose notesFormat is 'yxml-v1' (and the notes migrations await it
 * before converting a legacy doc). Every synchronous call path — toTree()
 * serialization, note writes, mobile note HTML rendering — runs after doc
 * open, so the module is already resident by then.
 *
 * If a sync call does arrive before the module (a race that should not
 * happen), we throw: toTree() wraps serialization in try/catch and falls back
 * to the cached/flat value, so the worst case is a stale note body for one
 * rebuild rather than silently clearing content.
 */

let mod = null;
let loading = null;

export function ensureNotesSerializer() {
  if (mod) return Promise.resolve(mod);
  loading ||= import("./notes-fragment-serializer.js").then((m) => {
    mod = m;
    return m;
  });
  return loading;
}

export function isNotesSerializerLoaded() {
  return !!mod;
}

function requireMod() {
  if (!mod) {
    throw new Error(
      "[NotesSerializer] called before the serializer chunk loaded — " +
      "ensureNotesSerializer() must be awaited during doc open",
    );
  }
  return mod;
}

export function fragmentToMarkdown(fragment) {
  return requireMod().fragmentToMarkdown(fragment);
}

export function fragmentToHtml(fragment) {
  return requireMod().fragmentToHtml(fragment);
}

export function markdownToFragment(fragment, markdown, ydoc) {
  return requireMod().markdownToFragment(fragment, markdown, ydoc);
}

export function fragmentIsMarkdownOnly(fragment) {
  return requireMod().fragmentIsMarkdownOnly(fragment);
}

export function destroySerializerEditor() {
  // Nothing to destroy if the chunk never loaded.
  if (mod) mod.destroySerializerEditor();
}
